import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "gatsby";
import {
    Container,
    Col,
    Nav,
    Navbar,
    NavDropdown,
    Row,
    Button,
} from "react-bootstrap";
import classNames from "classnames";
import { navigate } from "gatsby";
import { ChevronUp, ChevronDown } from "react-bootstrap-icons";

import { useNavItems } from "../hooks/useNavItems";
import { getNavItemProps } from "../utils/utils";

import Logo from "./Logo";

import MenuCollapseIcon from "../images/icons/menu-collapse.svg";
import MenuExpandIcon from "../images/icons/menu-expand.svg";

import "../styles/components/NavigationMenu.scss";

const ToggleWithIcon = ({ children, expanded, to, href }) => {
    const clickHandler = e => {
        e.preventDefault();
        if (!expanded) return;

        if (href) {
            window.location.href = href;
        }

        navigate(to);
    };

    if (to || href) {
        return (
            <Button
                variant="link"
                className="text-decoration-none p-0 text-reset"
                onClick={clickHandler}
            >
                {children}
            </Button>
        );
    }

    return children;
};

const NavDropdownWithIcon = ({ title, children, as, to, href, ...props }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <NavDropdown
            title={
                <>
                    <ToggleWithIcon
                        expanded={expanded}
                        as={as}
                        to={to}
                        href={href}
                    >
                        {title}
                    </ToggleWithIcon>
                    <span className="dropdown-toggle-with-icon-plus float-right d-inline-block d-md-none ms-2">
                        {expanded ? <ChevronUp /> : <ChevronDown />}
                    </span>
                </>
            }
            show={expanded}
            onToggle={() => setExpanded(!expanded)}
            onMouseEnter={() => setExpanded(true)}
            onMouseLeave={() => setExpanded(false)}
            {...props}
        >
            {children}
        </NavDropdown>
    );
};

const NavigationMenu = ({ activeKey }) => {
    const navItems = useNavItems({ showOnHeader: true });
    const [expanded, setExpanded] = useState(false);

    const isNavDropdownActive = children => {
        return children.find(
            item => item.page?.path?.split("/")[1] === activeKey
        );
    };

    return (
        <div className="sticky-top">
            <Navbar
                bg="light"
                expand="md"
                className="navigation-menu py-0 px-5"
            >
                <Container>
                    <Navbar.Brand to="/" as={Link} aria-label="Go to homepage">
                        <Logo variant="image" />
                    </Navbar.Brand>
                    <Navbar.Toggle
                        aria-controls="navigation-menu-navbar-nav"
                        onClick={() => setExpanded(!expanded)}
                    >
                        {expanded ? <MenuCollapseIcon /> : <MenuExpandIcon />}
                    </Navbar.Toggle>

                    <Navbar.Collapse id="navigation-menu-navbar-nav">
                        <Nav as={Row} className="ms-auto p-0">
                            <Col
                                xs={{ order: 2, span: 12 }}
                                md={{ order: 2, span: 12 }}
                                lg={{ order: 1, span: 8 }}
                                className="d-md-flex"
                            >
                                {navItems?.map(navItem => {
                                    const navItemsProps =
                                        getNavItemProps(navItem);
                                    if (!navItem.childrenItems?.length) {
                                        return (
                                            <Nav.Link
                                                {...navItemsProps}
                                                className={classNames(
                                                    navItem.cssClasses,
                                                    {
                                                        active:
                                                            activeKey ===
                                                            navItem?.page.path.split(
                                                                "/"
                                                            )[1],
                                                    }
                                                )}
                                            >
                                                {navItem.title}
                                            </Nav.Link>
                                        );
                                    }

                                    return (
                                        <NavDropdownWithIcon
                                            title={navItem.title}
                                            id={navItem.htmlId}
                                            key={navItem.id}
                                            {...navItemsProps}
                                            className={classNames(
                                                navItem.cssClasses,
                                                {
                                                    active: isNavDropdownActive(
                                                        navItem.childrenItems
                                                    ),
                                                }
                                            )}
                                        >
                                            {(navItem.page ||
                                                navItem.externalLink) && (
                                                <NavDropdown.Item
                                                    className="d-md-none"
                                                    {...navItemsProps}
                                                >
                                                    Overview
                                                </NavDropdown.Item>
                                            )}

                                            {navItem.childrenItems.map(item => {
                                                const itemProps =
                                                    getNavItemProps(item);

                                                return (
                                                    <NavDropdown.Item
                                                        {...itemProps}
                                                    >
                                                        {item.title}
                                                    </NavDropdown.Item>
                                                );
                                            })}
                                        </NavDropdownWithIcon>
                                    );
                                })}
                            </Col>

                            <Col
                                xs={{ order: 1, span: 12 }}
                                md={{ order: 1, span: 12 }}
                                lg={{ order: 2, span: 4 }}
                                className="d-md-flex mt-3 mt-md-0 justify-content-end"
                            >
                                <div className="vr py-3 d-none d-lg-block me-1" />

                                <Nav.Link
                                    href="https://apply.shiftmed.com/"
                                    className="text-primary"
                                >
                                    Apply Now
                                </Nav.Link>

                                <Nav.Link
                                    href="https://portal.shiftmed.com/login"
                                    className="text-primary"
                                >
                                    Portal Login
                                </Nav.Link>

                                <hr className="d-md-none w-25"></hr>
                            </Col>
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
        </div>
    );
};

NavigationMenu.propTypes = {
    activeKey: PropTypes.string,
};

export default NavigationMenu;
